/**
 * Ambient Runtime settings (AMB-0)
 *
 * Background jobs, run history, deliveries, and the workspace channel policy
 * for the ambient layer. Jobs execute server-side (supabase/functions/ambient);
 * this page configures and observes them — proof-of-life for every run and
 * delivery (build contract §2.4).
 *
 * Route: /prioritization/settings/ambient-runtime
 * Spec:  docs/9-exploration/AMBIENT-INTELLIGENCE-PRD.md §2
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Radar,
  Loader2,
  Lightbulb,
  Play,
  CheckCircle2,
  XCircle,
  MinusCircle,
  AlertTriangle,
  Bell,
  Mail,
  MessageSquare,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useAmbientRuntimeStore } from '../../../stores/prioritization/ambientRuntimeStore';
import {
  AMBIENT_JOB_META,
  defaultChannelPolicy,
  type AmbientJobType,
  type AmbientChannelPolicy,
} from '../../../lib/ambientRuntimeService';
import {
  judgeAmbientJob,
  judgeAmbientRuntime,
  type AmbientJobState,
} from '../../../lib/ambientRuntimeHealth';

const CSV_SCHEDULE_JOBS = new Set<AmbientJobType>(['csv_import', 'csv_export']);

function formatRelativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const future = diffMs < 0;
  const mins = Math.round(Math.abs(diffMs) / 60_000);
  const label =
    mins < 1 ? 'now'
    : mins < 60 ? `${mins}m`
    : mins < 60 * 24 ? `${Math.round(mins / 60)}h`
    : `${Math.round(mins / (60 * 24))}d`;
  if (label === 'now') return 'just now';
  return future ? `in ${label}` : `${label} ago`;
}

function cadenceLabel(minutes: number): string {
  if (minutes % 10080 === 0) return `every ${minutes === 10080 ? 'week' : `${minutes / 10080} weeks`}`;
  if (minutes % 1440 === 0) return `every ${minutes === 1440 ? 'day' : `${minutes / 1440} days`}`;
  if (minutes % 60 === 0) return `every ${minutes === 60 ? 'hour' : `${minutes / 60} hours`}`;
  return `every ${minutes} min`;
}

/**
 * The health verdict's colour. `healthy` and `disabled` stay in tertiary grey
 * on purpose: only a state that needs a person gets a colour, or the colour
 * stops meaning anything.
 */
const STATE_TEXT: Record<AmbientJobState, string> = {
  healthy: 'text-[var(--text-ter)]',
  disabled: 'text-[var(--text-ter)]',
  overdue: 'text-[var(--coral)]',
  never_run: 'text-[var(--coral)]',
  failing: 'text-[var(--coral)]',
};

function StatusIcon({ status }: { status: string | null }) {
  if (status === 'completed' || status === 'sent') {
    return <CheckCircle2 className="h-4 w-4 text-[var(--green)]" />;
  }
  if (status === 'failed') return <XCircle className="h-4 w-4 text-[var(--coral)]" />;
  if (status === 'skipped') return <MinusCircle className="h-4 w-4 text-[var(--text-ter)]" />;
  if (status === 'running') return <Loader2 className="h-4 w-4 animate-spin text-primary-600" />;
  return <MinusCircle className="h-4 w-4 text-[var(--text-ter)]" />;
}

function Toggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-[var(--bg-raised)] transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

const CHANNEL_ROWS: Array<{
  key: keyof Pick<AmbientChannelPolicy, 'inAppEnabled' | 'emailDigestEnabled' | 'slackEnabled'>;
  name: string;
  description: string;
  icon: typeof Bell;
}> = [
  {
    key: 'inAppEnabled',
    name: 'In-app notifications',
    description: 'Deliver ambient findings and digests as in-app notifications (default on)',
    icon: Bell,
  },
  {
    key: 'emailDigestEnabled',
    name: 'Email digest',
    description: 'Weekly digest to workspace admins by email (default on)',
    icon: Mail,
  },
  {
    key: 'slackEnabled',
    name: 'Slack',
    description:
      'Post ambient deliveries to the workspace default Slack channel (set in Connectors → Configure)',
    icon: MessageSquare,
  },
];

export default function AmbientRuntime() {
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);
  const addNotification = useUIStore((s) => s.addNotification);

  const jobs = useAmbientRuntimeStore((s) => s.jobs);
  const policy = useAmbientRuntimeStore((s) => s.policy);
  const deliveries = useAmbientRuntimeStore((s) => s.deliveries);
  const runLogs = useAmbientRuntimeStore((s) => s.runLogs);
  const isLoading = useAmbientRuntimeStore((s) => s.isLoading);
  const isSaving = useAmbientRuntimeStore((s) => s.isSaving);
  const runningJobType = useAmbientRuntimeStore((s) => s.runningJobType);
  const error = useAmbientRuntimeStore((s) => s.error);
  const fetchAll = useAmbientRuntimeStore((s) => s.fetchAll);
  const toggleJob = useAmbientRuntimeStore((s) => s.toggleJob);
  const savePolicy = useAmbientRuntimeStore((s) => s.savePolicy);
  const runNow = useAmbientRuntimeStore((s) => s.runNow);

  useEffect(() => {
    if (workspaceId) void fetchAll(workspaceId);
  }, [workspaceId, fetchAll]);

  // One clock for the whole render, so two rows judged in the same paint can
  // never disagree about what "now" is. Recomputed per render, which is what
  // we want: the verdict should move when the page refetches.
  const nowIso = new Date().toISOString();
  const runtimeHealth = judgeAmbientRuntime(jobs, nowIso);

  if (!workspaceId) {
    return (
      <div className="card bg-[var(--mustard-dim)] border-[var(--mustard)] text-sm text-[var(--mustard)]">
        Select a workspace to configure the Ambient Runtime.
      </div>
    );
  }

  const effectivePolicy = policy ?? defaultChannelPolicy(workspaceId);
  const jobTypes: AmbientJobType[] = [
    'sync_dispatch',
    'weekly_digest',
    'intelligence_scan',
    'pulse_delivery',
    'decision_narrative',
    'evidence_brief',
    'outcome_ledger',
    'beacon_scan',
    'review_due',
    'csv_import',
    'csv_export',
    'automation_sweep',
  ];

  const handleToggleJob = async (jobType: AmbientJobType, enabled: boolean) => {
    const res = await toggleJob(workspaceId, jobType, enabled, userId ?? undefined);
    if (res.success) {
      addNotification({
        type: 'success',
        title: `${AMBIENT_JOB_META[jobType].name} ${enabled ? 'enabled' : 'disabled'}`,
        message: '',
        duration: 2000,
      });
    }
  };

  const handleTogglePolicy = async (
    key: (typeof CHANNEL_ROWS)[number]['key'],
    next: boolean,
  ) => {
    const res = await savePolicy({ ...effectivePolicy, [key]: next }, userId ?? undefined);
    if (res.success) {
      addNotification({ type: 'success', title: 'Channel policy saved', message: '', duration: 2000 });
    }
  };

  const handleRunNow = async (jobType: AmbientJobType) => {
    const res = await runNow(workspaceId, jobType);
    if (res.success) {
      addNotification({
        type: 'success',
        title: `${AMBIENT_JOB_META[jobType].name} ran`,
        message: 'See run history and deliveries below.',
        duration: 3000,
      });
    } else {
      addNotification({
        type: 'error',
        title: 'Run failed',
        message: res.error ?? 'Unknown error',
        duration: 5000,
      });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Radar className="h-4 w-4 text-primary-700" />}
        iconBg="bg-primary-100"
        title="Ambient Runtime"
        subtitle="Background jobs that work while nobody is looking — and a log of everything they deliver"
      />

      {error && (
        <div className="card bg-[var(--coral-dim)] border-[var(--coral)] text-sm text-[var(--coral)]">{error}</div>
      )}

      {isLoading ? (
        <div className="card flex items-center gap-2 text-sm text-[var(--text-ter)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading configuration…
        </div>
      ) : (
        <>
          {/* Background jobs */}
          <div className="card space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-[var(--text-pri)]">Background jobs</h2>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">
                Jobs run on their own cadence once the scheduler tick is wired (see the ambient
                function README). "Run now" executes a job immediately.
              </p>
            </div>
            {runtimeHealth.runtimeDark && runtimeHealth.headline && (
              <div className="flex gap-3 border border-[var(--coral)] bg-[var(--coral-dim)] px-4 py-3">
                <AlertTriangle
                  className="h-4 w-4 text-[var(--coral)] flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--coral)]">
                    The ambient runtime is not running
                  </p>
                  <p className="mt-0.5 text-sm text-[var(--text-sec)]">{runtimeHealth.headline}</p>
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Every job below is enabled and overdue, which is one fault rather than several:
                    the tick is not arriving. "Run now" still works and is unaffected.
                  </p>
                </div>
              </div>
            )}
            <div className="divide-y divide-[var(--rule)]">
              {jobTypes.map((jobType) => {
                const job = jobs.find((j) => j.jobType === jobType);
                const meta = AMBIENT_JOB_META[jobType];
                const enabled = job?.enabled ?? false;
                return (
                  <div key={jobType} className="flex items-start gap-4 py-3">
                    <Toggle
                      checked={enabled}
                      disabled={isSaving}
                      onChange={(next) => void handleToggleJob(jobType, next)}
                      label={`Toggle ${meta.name}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[var(--text-pri)]">{meta.name}</span>
                        {job && (
                          <span className="text-xs text-[var(--text-ter)]">
                            {cadenceLabel(job.cadenceMinutes)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[var(--text-ter)] mt-0.5">{meta.description}</p>
                      {CSV_SCHEDULE_JOBS.has(jobType) && (
                        <p className="text-xs text-[var(--text-ter)] mt-1">
                          {job?.profileId
                            ? 'Runs a Spreadsheet sync profile — its schedule is set on that profile.'
                            : 'No profile bound yet.'}{' '}
                          <Link
                            to="/prioritization/settings/csv-citizenship"
                            className="font-medium text-primary-600 hover:underline"
                          >
                            Set the schedule in Spreadsheet sync →
                          </Link>
                        </p>
                      )}
                      {job && (() => {
                        // The raw timestamps were always rendered here, in
                        // tertiary grey, with no threshold: "last run 53d ago ·
                        // next 53d ago" read exactly like a healthy job. What
                        // was missing was never the data — it was the verdict.
                        const health = judgeAmbientJob(job, nowIso);
                        return (
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-[var(--text-ter)]">
                            <span
                              className={`inline-flex items-center gap-1 font-medium ${STATE_TEXT[health.state]}`}
                            >
                              {health.needsAttention ? (
                                <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                              ) : (
                                <StatusIcon status={job.lastStatus} />
                              )}
                              {health.label}
                            </span>
                            <span className={STATE_TEXT[health.state]}>{health.sentence}</span>
                            {enabled && !health.needsAttention && (
                              <span>next {formatRelativeTime(job.nextRunAt)}</span>
                            )}
                            {job.lastError && health.state !== 'failing' && (
                              <span className="text-[var(--coral)] truncate">{job.lastError}</span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <button
                      type="button"
                      disabled={!enabled || runningJobType !== null}
                      onClick={() => void handleRunNow(jobType)}
                      className="inline-flex items-center gap-1.5 border border-[var(--rule)] px-2.5 py-1.5 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {runningJobType === jobType ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Play className="h-3.5 w-3.5" />
                      )}
                      Run now
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Channel policy */}
          <div className="card space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-[var(--text-pri)]">Delivery channels</h2>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">
                Where ambient deliveries go. Capped at {effectivePolicy.dailyDeliveryCap} deliveries
                per 24 hours — moments, not feeds.
              </p>
            </div>
            <div className="divide-y divide-[var(--rule)]">
              {CHANNEL_ROWS.map(({ key, name, description, icon: Icon }) => (
                <div key={key} className="flex items-center gap-4 py-3">
                  <Icon className="h-4 w-4 text-[var(--text-ter)] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className="text-sm font-medium text-[var(--text-pri)]">{name}</span>
                    <p className="text-xs text-[var(--text-ter)] mt-0.5">{description}</p>
                  </div>
                  <Toggle
                    checked={effectivePolicy[key]}
                    disabled={isSaving}
                    onChange={(next) => void handleTogglePolicy(key, next)}
                    label={`Toggle ${name}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Run history */}
          <div className="card space-y-3">
            <h2 className="text-sm font-semibold text-[var(--text-pri)]">Run history</h2>
            {runLogs.length === 0 ? (
              <p className="text-xs text-[var(--text-ter)]">No runs yet.</p>
            ) : (
              <div className="divide-y divide-[var(--rule)]">
                {runLogs.map((log) => (
                  <div key={log.id} className="flex items-center gap-3 py-2 text-xs">
                    <StatusIcon status={log.status} />
                    <span className="font-medium text-[var(--text-pri)]">
                      {AMBIENT_JOB_META[
                        log.agentType.replace('ambient_', '') as AmbientJobType
                      ]?.name ?? log.agentType}
                    </span>
                    <span className="text-[var(--text-ter)]">{log.triggeredBy}</span>
                    <span className="text-[var(--text-ter)]">{formatRelativeTime(log.startedAt)}</span>
                    {log.stats.processed !== undefined && (
                      <span className="text-[var(--text-ter)]">{log.stats.processed} processed</span>
                    )}
                    {log.errorMessage && (
                      <span className="text-[var(--coral)] truncate">{log.errorMessage}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Deliveries */}
          <div className="card space-y-3">
            <h2 className="text-sm font-semibold text-[var(--text-pri)]">Recent deliveries</h2>
            {deliveries.length === 0 ? (
              <p className="text-xs text-[var(--text-ter)]">
                Nothing delivered yet. Every ambient delivery — in-app, email, or Slack — is logged
                here.
              </p>
            ) : (
              <div className="divide-y divide-[var(--rule)]">
                {deliveries.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 py-2 text-xs">
                    <StatusIcon status={d.status} />
                    <span className="uppercase tracking-wide text-[var(--text-ter)] w-12 shrink-0">
                      {d.channel === 'in_app' ? 'in-app' : d.channel}
                    </span>
                    <span className="font-medium text-[var(--text-pri)] truncate">{d.subject}</span>
                    <span className="text-[var(--text-ter)] shrink-0">{formatRelativeTime(d.createdAt)}</span>
                    {d.errorMessage && (
                      <span className="text-[var(--coral)] truncate">{d.errorMessage}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* How it works */}
          <div className="card bg-[var(--slate-dim)] border-[var(--slate)]">
            <div className="flex gap-3">
              <Lightbulb className="h-4 w-4 text-[var(--text-ter)] shrink-0 mt-0.5" />
              <div className="text-xs text-[var(--text-sec)] space-y-1">
                <p className="font-semibold">How the Ambient Runtime works</p>
                <p>
                  Jobs run server-side on a schedule and only for workspaces with this module
                  enabled. They propose, summarize, and deliver — they never change rankings,
                  cycles, or any governance state (voice, never authority). Every run and every
                  delivery is logged here so there is no invisible background activity.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
